<template>
  <div v-element-loader="{ endpoint: ['cart/addToCart', 'cart/changeQuantity'], matcher: matchUrlChangeQuantity }" class="cart-product">
    <oie-action-wrapper class="cart-product__actions">
      <template v-if="hasMyMachines" #left>
        <div class="cart-product__machine-actions">
          <oie-my-machines-selector-slide-in
            :tooltip="cartRow.machineOwnerId || cartRow.machineId ? tStatic('changeMachineTooltip'): ''"
            :text="cartRow.machineOwnerId || cartRow.machineId ? tStatic('changeMachine') : tStatic('selectMachine')"
            @select-owner="handleSelection($event)"
            @select-machine="handleSelection($event)">
          </oie-my-machines-selector-slide-in>
        </div>
        <oie-base-action
          v-if="cartRow.machineId || cartRow.machineOwnerId"
          v-tooltip.right="tStatic('clearMachineTooltip')"
          class="cart-product__machine-actions-cancel"
          theme="destructive"
          @click="handleSelection(null)">
          <template #iconPrepend>
            <oie-base-icon icon="close" size="s"></oie-base-icon>
          </template>
        </oie-base-action>
      </template>
      <template #right>
        <oie-base-action theme="destructive" @click="deleteItem()">
          <oie-base-icon icon="trash-can" size="xxs"></oie-base-icon>
        </oie-base-action>
      </template>
    </oie-action-wrapper>
    <div v-if="cartRow.machineId || cartRow.machineOwnerId" class="cart-product__machine-connection">
      {{ cartRow.machineOwnerName }}
      <span v-if="cartRow.machineOwnerId && cartRow.machineId"> - </span>
      {{ cartRow.machineName }}
    </div>
    <div class="cart-product__container">
      <oie-base-image
        :images="cartRow.item.image ? `${getAssetPath(cartRow.item.image)}?preset=ProductSmall&watermark=OIEWatermarkSmall` : `${getAssetPath('/globalassets/missing404/404.png')}?preset=ProductThumb`"
        :alt-text="cartRow.item.name"
        image-width="3.75rem"
        image-height="3.75rem"
        class="cart-product__image"></oie-base-image>

      <div class="cart-product__information-container">
        <div class="cart-product__top">
          <component
            :is="disableInternalItem ? 'span' : 'a'"
            :href="cartRow.item.url"
            class="cart-product__details"
            @click="closeCart()">
            {{ cartRow.item.name }}

            <div class="cart-product__details-article-number" :data-pw="`cart-row-itemid-${itemId}`">
              {{ `${tStatic('headerCartItemNumberLabel')} ${cartRow.itemId}` }}
            </div>
          </component>

          <div v-if="cartRow.stock - cartRow.quantity < 0" class="cart-product__details-exceded-amount">
            {{ tStatic('exceededStockLevelLabel') }}
          </div>
        </div>

        <div class="cart-product__quantity-related">
          <oie-product-quantity
            :disabled="disableInternalItem"
            css-classes="cart-product__quantity"
            :cart-row="cartRow"
            :cart-name="cartName"></oie-product-quantity>

          <div v-if="!isQuoteItem" :class="cartRow.isDiscounted ? 'cart-product__price discounted' : 'cart-product__price'">
            {{ formatPrice(cartRow.price) }} / {{ tStatic('headerCartItemAmountLabel') }}
          </div>
          <p v-else class="cart-product__price">
            {{ tStatic('priceUnavailable') }}
          </p>
        </div>
      </div>
    </div>

    <oie-comment
      :title="tStatic('headerCartNotePlaceholder')"
      :initial-value="cartRow.comment"
      class="cart-product__comment"
      @save="(...args) => saveComment(...args)"
      @delete="(...args) => deleteComment(...args)"></oie-comment>
  </div>
</template>

<script>
import accordion from '@/mixins/accordion.js'
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import gtm from '@/core/enhanced-ecommerce-gtm'
import cartService from '@/core/cart-service'
import { productStatuses } from '@/variables/product-statuses'

export default {
  mixins: [accordion],
  props: {
    cartRow: {
      type: Object,
      required: true
    },
    cartName: {
      type: String,
      required: true
    },
    useForNotification: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    comment: '',
    commentIsActive: false,
    commentSaveDisabled: true
  }),
  computed: {
    ...mapGetters(['enableGtm']),
    ...mapGetters('myMachines', ['hasMyMachines']),
    isSuperUser() {
      return (
        this.clientModel.impersonating !== null || this.clientModel.isSuperUser
      )
    },
    isQuoteItem() {
      return this.cartRow.publicationStatusCode === productStatuses.noPrice
    },
    isInternalItem() {
      return this.cartRow.publicationStatusCode === productStatuses.internal
    },
    disableInternalItem() {
      return this.isInternalItem && !this.isSuperUser
    }
  },
  methods: {
    ...mapActions('commerce', ['deleteFromCart', 'saveNote', 'setCart']),
    ...mapActions('ui', ['toggleValue']),
    ...mapActions('myMachines', ['getMyMachines']),
    matchUrlChangeQuantity(data) {
      return _.some(data.rows, item => item.itemId === this.cartRow.itemId && !this.cartRow.comment && !this.cartRow.machineId && !this.cartRow.machineOwnerId)
    },
    closeCart() {
      this.toggleValue({ key: 'isCartOpen', data: false })
    },
    deleteItem() {
      const cartRowToDelete = this.cartRow
      this.deleteFromCart({ lineItemId: cartRowToDelete.id }).then(() => {
        if (this.enableGtm) {
          const gtmObject = gtm.createGtmItem(
            cartRowToDelete.item.itemId,
            cartRowToDelete.item.name,
            null,
            cartRowToDelete.item.purchasedOnCategory,
            this.formatPriceWithoutCurrency(cartRowToDelete.priceExVat),
            cartRowToDelete.quantity,
            null)

          gtm.removeFromCart(gtmObject)
        }
      })
    },
    async handleSelection($event) {
      const { data } = await cartService.markMachineForRow($event, this.cartRow.id)
      this.setCart(data.data)
    },
    saveComment(comment, callback) {
      const { id } = this.cartRow
      cartService.saveNote(id, comment).then(() => {
        this.cartRow.comment = comment
        callback.success()
      }).catch(() => callback.error())
    },

    deleteComment(callback) {
      const { id } = this.cartRow
      cartService.deleteNote(id).then(() => {
        this.cartRow.comment = ''
        callback.success()
      }).catch(() => callback.error())
    }
  }
}
</script>

<style lang="scss">
.cart-product {
  padding: measure(quarter);
  border: 1px solid color(border-gray);
  transition: border-color 140ms ease-out, box-shadow 200ms ease-out, background-color 140ms ease-out;
}

.cart-product__actions {
  margin-bottom: measure(quarter);
  flex-wrap: wrap;
}

.cart-product__machine-actions {
  display: flex;
  gap: 0.5rem;
}

.cart-product__machine-actions-cancel {
  padding: 3px;

  > svg:first-child:not(:only-child) {
    margin-right: 0;
  }
}

.cart-product__machine-connection {
  font-style: italic;
  margin-bottom: measure(quarter);
}

.cart-product--new {
  border-color: color(border-gray-2);
  box-shadow: 0px 0px 6px 1px rgba(93, 92, 92, 0.5);
  background-color: color(background-gray-5);
  transition: border-color 140ms ease-in, box-shadow 200ms ease-in, background-color 140ms ease-in;
}

.cart-product__container {
  display: flex;
  flex-wrap: nowrap;
}

.cart-product__image {
  width: 3.75rem;
  height: 3.75rem;
  flex-shrink: 0;
  align-self: flex-start;
}

.cart-product__information-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  margin-left: measure(quarter);
}

.cart-product__top {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-bottom: measure(quarter);
}

.cart-product__details {
  @include caption-semi-bold;
  color: color(text-blue);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.cart-product__details-article-number {
  color: color(text);
}

.cart-product__details-exceded-amount {
  @include caption-bold;
  margin-top: auto;
}

.cart-product--warning .cart-product__details-exceded-amount {
  align-self: start;
  padding: 0 measure(eighth);
  background-color: color(yellow-2);
  border: 1px solid color(yellow-1);
}

.cart-product__quantity-related {
  display: flex;
  align-items: center;
}

.cart-product__price {
  margin-left: auto;

  &.discounted {
    color:red;
  }
}

.cart-product__comment {
  width: 100%;
  margin-top: measure(half);
}
.cart-product__assemblynote {
  width: 100%;
  margin-top: measure(quarter);
}

</style>
